/**
 * Круглый аватар с инициалами.
 *
 * Фотография собеседника, если она есть, отдаётся нашим же эндпоинтом
 * /api/chats/:id/photo и в базе не оседает — TDLib остаётся единственным
 * источником правды. Инициалы служат запасным вариантом: фотографии нет,
 * не загрузилась, либо чат таковой не имеет.
 *
 * Цвет выводится детерминированно из идентификатора, а не выбирается случайно.
 * Один и тот же чат всегда одного цвета, и в длинном списке глаз находит его
 * быстрее, чем успевает прочитать название.
 *
 * Отдельно важно для маскирования (§7 ТЗ): инициалы берутся из уже пришедшей с
 * сервера подписи. Стажёру сервер прислал «Account 01» — в аватаре окажется
 * «A0», а не первая буква настоящего имени. Никакой identity компонент не
 * получает и получить не может.
 */

import { useState } from "react";
import { cn } from "@/lib/utils";
import { seedColor } from "../lib/seedColor.js";

interface AvatarProps {
  /** Подпись, из которой берутся инициалы. */
  name: string;
  /** Идентификатор для выбора цвета — стабильный между перерисовками. */
  seed: string;
  size?: "xs" | "sm" | "md" | "chat" | "lg";
  className?: string;
  /**
   * Адрес фотографии. Если её нет или она не загрузилась, остаются инициалы.
   *
   * Речь только о фотографии СОБЕСЕДНИКА. Аватар подключённого аккаунта сюда
   * не передаётся никогда: для стажёра это была бы прямая утечка того, что
   * скрывает alias (§7 ТЗ).
   */
  photoUrl?: string | null;
}

/*
 * Палитра и выбор цвета переехали в lib/seedColor.
 *
 * Тем же цветом теперь помечаются аккаунты в списке чатов, и две копии
 * палитры разошлись бы: один аккаунт получил бы на аватаре один цвет, а в
 * метке другой — то есть ровно то, ради чего цвет вводится, перестало бы
 * работать.
 *
 * Восемь тонов остались, и правило «один акцент на систему» здесь по-прежнему
 * сознательно ослаблено: различимость в длинном списке важнее строгости.
 */

const SIZES = {
  xs: "size-7 text-[10px]",
  sm: "size-9 text-xs",
  md: "size-11 text-[15px]",
  /** Строка списка чатов — компактная, как в Telegram Desktop. */
  chat: "size-12 text-[17px]",
  /** Крупный аватар — шапка чата, карточка. */
  lg: "size-[54px] text-[19px]",
} as const;

export function Avatar({
  name,
  seed,
  size = "md",
  className,
  photoUrl,
}: AvatarProps) {
  const [broken, setBroken] = useState(false);

  if (photoUrl && !broken) {
    return (
      <img
        src={photoUrl}
        alt=""
        aria-hidden
        loading="lazy"
        onError={() => setBroken(true)}
        className={cn(
          "flex-none rounded-full object-cover",
          SIZES[size],
          className,
        )}
      />
    );
  }

  return (
    <span
      aria-hidden
      style={{ background: seedColor(seed) }}
      className={cn(
        "grid flex-none select-none place-items-center rounded-full font-semibold text-white",
        SIZES[size],
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}

/** Не более двух букв: три уже не читаются в круге 32 пикселя. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";

  const first = [...(words[0] ?? "")][0] ?? "";
  const second = [...(words[1] ?? "")][0] ?? "";

  return (first + second).toUpperCase();
}

/**
 * Хеш строки в индекс палитры. Алгоритм — обычный djb2-подобный: нужен
 * стабильный разброс, а не криптостойкость.
 */
